import { useEffect } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { StepContent } from '../StepContent';
import { HdeOptionList } from '../HDEOptionList';
import {
    getHdeStepSectionClassName,
    getHdeStepTitleClassName,
} from '../hde-option-styles';

type ApplicationType = 'Homeowner' | 'Professional' | 'Exhibitor';

interface ExistingSubmissionStepData {
    /** First name from the existing submission, for the greeting. */
    firstName?: string | null;
    /** Application type of the existing submission. */
    existingType?: ApplicationType | null;
    /** Application type the member just selected to enter this sub-form. */
    selectedType?: ApplicationType;
    /** True when the selected type matches the existing submission. */
    sameType: boolean;
    /** Keep the existing registration (switches sub-form if a different type). */
    onContinueExisting: () => void;
    /** Start a fresh registration as the selected type, discarding previous answers. */
    onSwitchToSelected: () => void;
    /** Close the modal, leaving the existing registration untouched. */
    onCancel: () => void;
}

const withArticle = (type: string): string =>
    `a${/^[AEIOU]/i.test(type) ? 'n' : ''} ${type}`;

/**
 * Gate shown as the first sub-form step when the logged-in member already has a
 * submission for this event. Offers: continue the existing registration, switch to
 * the newly-selected type (discarding previous answers), or cancel.
 */
export function ExistingSubmissionStep({
    setIsValid,
    initialData,
}: StepContentProps) {
    const data = initialData as ExistingSubmissionStepData | undefined;

    useEffect(() => {
        setIsValid(true);
    }, [setIsValid]);

    const firstName = data?.firstName?.trim();
    const existingType = data?.existingType ?? null;
    const selectedType = data?.selectedType ?? null;
    const sameType = data?.sameType ?? false;

    const greeting = firstName
        ? `Hi ${firstName}, you've already registered for this event.`
        : "You've already registered for this event.";

    // A member can only hold one registration per event. Same type => the only action
    // is to update it. Different type => keep the existing one, or replace it with the
    // newly-selected type (still a single registration).
    const isDifferentType =
        !sameType && Boolean(existingType) && Boolean(selectedType);

    const description = isDifferentType
        ? `You're already registered as ${withArticle(
              existingType as string,
          )}. You can keep that registration, or switch it to a ${selectedType} registration instead — your previous answers will be discarded.`
        : `You're already registered as ${withArticle(
              existingType ?? 'guest',
          )}. Continue to review and update your details.`;

    const options = isDifferentType
        ? [
              { value: 'continue', label: `Continue as ${existingType}` },
              { value: 'switch', label: `Switch to ${selectedType}` },
              { value: 'cancel', label: 'Cancel' },
          ]
        : [
              { value: 'continue', label: 'Update my registration' },
              { value: 'cancel', label: 'Cancel' },
          ];

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-[38px]">
            <StepContent variant="guest">
                <div className={getHdeStepSectionClassName()}>
                    <label className={getHdeStepTitleClassName()}>
                        {greeting}
                    </label>
                    <p className="max-w-[465px] text-[0.85rem] leading-snug text-[#5c5c5c]">
                        {description}
                    </p>
                    <HdeOptionList
                        label=""
                        mode="single"
                        appearance="button"
                        options={options}
                        value={null}
                        onChange={() => {}}
                        onSelectAndAdvance={(value) => {
                            if (value === 'continue') {
                                data?.onContinueExisting();
                            } else if (value === 'switch') {
                                data?.onSwitchToSelected();
                            } else {
                                data?.onCancel();
                            }
                        }}
                    />
                </div>
            </StepContent>
        </div>
    );
}
